import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BandwidthPanel, DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, Stat, StateView, formatBytes, useAuth, useToast } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";

// The remaining per-tenant surfaces: database grants, SFTP access, live processes and usage.

interface Proc {
  pid: number;
  command: string;
  state: string;
  rss_bytes: number;
  cpu_seconds: number;
  io_bytes: number;
  threads: number;
}
interface Processes {
  node: string;
  system_user: string;
  uid: number;
  processes: number;
  threads: number;
  rss_bytes: number;
  cpu_seconds: number;
  io_bytes: number;
  list: Proc[] | null;
}
interface LimitRow {
  key: string;
  label: string;
  used: number;
  limit: number;
  percent: number;
  unit: string;
  limit_from?: string;
  over_by?: number;
  /** What the used figure does not include (a source not measured on some node). */
  incomplete?: string[] | null;
}
interface Usage {
  against_limits: LimitRow[] | null;
  unmeasured: string[] | null;
  days: number;
}
interface DbGrant {
  user: string;
  label: string;
  privilege: string;
}
interface Db {
  name: string;
  label: string;
  user: string;
  grants: DbGrant[] | null;
}
interface DbUser {
  user: string;
  label: string;
  owner: boolean;
}

/** Who may reach which database. */
export function GrantsTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [revoke, setRevoke] = React.useState<{ db: Db; grant: DbGrant } | null>(null);
  const dbBase = `/tenants/${tenantId}/databases`;

  const dbs = useQuery({
    queryKey: ["tenant-databases", tenantId],
    queryFn: () => client.apiFetch<{ items: Db[] | null }>(dbBase),
  });
  const users = useQuery({
    queryKey: ["tenant-db-users", tenantId],
    queryFn: () => client.apiFetch<{ items: DbUser[] | null }>(`/tenants/${tenantId}/db-users`),
  });

  const [grantFor, setGrantFor] = React.useState<Db | null>(null);
  const grant = useCreate({
    title: grantFor ? `Grant access to ${grantFor.name}` : "Grant access",
    description:
      "Read-only can select; read-write can also change rows; all adds schema changes. The database's own owner user always has full access and is not listed.",
    submitLabel: "Grant access",
    fields: [
      {
        key: "user",
        label: "Database user",
        required: true,
        options: (users.data?.items ?? [])
          .filter((u) => !u.owner)
          .map((u) => ({ value: u.user, label: u.user })),
      },
      {
        key: "privilege",
        label: "Privilege",
        type: "enum",
        options: [
          { value: "readonly", label: "Read only" },
          { value: "readwrite", label: "Read and write" },
          { value: "all", label: "All (including schema changes)" },
        ],
        defaultValue: "readwrite",
      },
    ],
    run: (v) =>
      client.apiFetch<unknown>(`${dbBase}/${encodeURIComponent(grantFor?.name || "")}/grants`, {
        method: "POST",
        body: JSON.stringify({ user: v.user, privilege: v.privilege }),
      }),
    invalidates: [["tenant-databases", tenantId]],
    success: (v) => `${v.user} granted ${v.privilege}`,
  });

  const doRevoke = useMutation({
    mutationFn: ({ db, grant }: { db: Db; grant: DbGrant }) =>
      client.apiFetch<unknown>(
        `${dbBase}/${encodeURIComponent(db.name)}/grants/${encodeURIComponent(grant.user)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      notify({ kind: "success", title: "Access revoked" });
      void qc.invalidateQueries({ queryKey: ["tenant-databases", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Revoke failed", body: e instanceof Error ? e.message : String(e) }),
  });

  return (
    <Section title="Database access" bodyClassName="p-0">
      <div className="space-y-4 p-4">
        <StateView loading={dbs.isLoading} error={dbs.error} errorContext="database access">
          <>
            {(dbs.data?.items ?? []).length === 0 && <p className="text-[12.5px] text-muted">No databases.</p>}
            {(dbs.data?.items ?? []).map((db) => (
              <div key={db.name} className="overflow-hidden rounded-lg border border-line">
                <div className="flex items-center justify-between border-b border-line px-3 py-2">
                  <div>
                    <code className="font-mono text-[12.5px]">{db.name}</code>
                    {/* The owner is granted implicitly and cannot be revoked. Listing it as a
                        removable grant would offer an action that can only fail. */}
                    <span className="ml-2 text-[11.5px] text-muted">owner {db.user}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setGrantFor(db);
                      grant.open();
                    }}
                  >
                    Grant access
                  </Button>
                </div>
                {(db.grants ?? []).length === 0 ? (
                  <p className="px-3 py-2 text-[12px] text-muted">No additional users — only the owner can connect.</p>
                ) : (
                  (db.grants ?? []).map((g) => (
                    <div key={g.user} className="flex items-center justify-between border-b border-line px-3 py-1.5 last:border-b-0">
                      <span className="text-[12.5px]">
                        <code className="font-mono">{g.user}</code>
                        <StatusPill tone={g.privilege === "readonly" ? "neutral" : "warn"}>{g.privilege}</StatusPill>
                      </span>
                      <Button size="sm" variant="danger" onClick={() => setRevoke({ db, grant: g })}>
                        Revoke
                      </Button>
                    </div>
                  ))
                )}
              </div>
            ))}
          </>
        </StateView>
      </div>
      {grant.dialog}
      <ConfirmDialog
        open={revoke !== null}
        onOpenChange={(o) => !o && setRevoke(null)}
        title={`Revoke ${revoke?.grant.user} from ${revoke?.db.name}?`}
        destructive
        confirmLabel="Revoke"
        pending={doRevoke.isPending}
        onConfirm={() => {
          if (revoke) doRevoke.mutate(revoke);
          setRevoke(null);
        }}
        body={<p>Anything connecting as that user to this database stops working immediately. Other databases are unaffected.</p>}
      />
    </Section>
  );
}

/** SFTP: a REAL system credential, gated on the plan. */
export function SftpTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [secret, setSecret] = React.useState<string | null>(null);
  const [confirmApply, setConfirmApply] = React.useState(false);

  const setPw = useCreate({
    title: "Set the SFTP password",
    description:
      "This is a real login on the node. The account is chrooted to its home and never gets a shell. Refused while the plan does not grant SFTP — a password on a locked account is a credential waiting for someone to unlock it.",
    submitLabel: "Set password",
    fields: [{ key: "password", label: "Password", type: "password", required: true }],
    run: (v) =>
      client
        .apiFetch<unknown>(`/tenants/${tenantId}/sftp/password`, {
          method: "POST",
          body: JSON.stringify({ password: v.password }),
        })
        .then((r) => {
          setSecret(null);
          return r;
        }),
    invalidates: [["tenant", tenantId]],
    success: () => "SFTP password set",
  });

  const apply = useMutation({
    mutationFn: () => client.apiFetch<Record<string, unknown>>(`/tenants/${tenantId}/sftp/apply`, { method: "POST" }),
    onSuccess: (r) => notify({ kind: "success", title: "Account reconciled with its plan", body: JSON.stringify(r).slice(0, 160) }),
    onError: (e) => notify({ kind: "error", title: "Apply failed", body: e instanceof Error ? e.message : String(e) }),
  });

  return (
    <Section
      title="SFTP access"
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={setPw.open}>Set password</Button>
          <Button size="sm" variant="ghost" disabled={apply.isPending} onClick={() => setConfirmApply(true)}>
            {apply.isPending ? "Applying…" : "Match plan"}
          </Button>
        </div>
      }
    >
      <p className="text-[12.5px] leading-relaxed text-muted">
        SFTP is a real Linux account on the node, chrooted to its home with no shell. The plan's SFTP feature and the
        lock on that account are <b>the same fact</b> — “Match plan” is what makes them agree, and is what to run after
        changing a plan. Setting a password is refused while the plan does not grant SFTP.
      </p>
      {secret && <Panel className="mt-3 p-3 font-mono text-[12.5px]">{secret}</Panel>}
      {setPw.dialog}
      <ConfirmDialog
        open={confirmApply}
        onOpenChange={setConfirmApply}
        title="Make the account match its plan?"
        confirmLabel="Apply"
        pending={apply.isPending}
        onConfirm={() => {
          setConfirmApply(false);
          apply.mutate();
        }}
        body={
          <p>
            Idempotent. If the plan grants SFTP the account is unlocked; if it does not, the account is locked and any
            existing SFTP session stops working.
          </p>
        }
      />
    </Section>
  );
}

/** What this account is actually running on this node. */
export function ProcessesTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tenant-processes", tenantId],
    queryFn: () => client.apiFetch<Processes>(`/tenants/${tenantId}/processes`),
    refetchInterval: 15000,
  });
  const d = q.data;

  const cols: DataListColumn<Proc>[] = [
    { key: "pid", label: "PID", sortable: true, value: (p) => p.pid },
    { key: "command", label: "Command", value: (p) => p.command, render: (p) => <span className="break-all font-mono text-[11.5px]">{p.command}</span> },
    { key: "state", label: "State", sortable: true, value: (p) => p.state },
    { key: "rss_bytes", label: "Memory", sortable: true, value: (p) => p.rss_bytes, render: (p) => formatBytes(p.rss_bytes) },
    { key: "cpu_seconds", label: "CPU", sortable: true, value: (p) => p.cpu_seconds, render: (p) => `${p.cpu_seconds.toFixed(1)}s` },
    { key: "threads", label: "Threads", sortable: true, value: (p) => p.threads },
  ];

  return (
    <Section title="Running processes" hint={d ? `on ${d.node}` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="running processes">
          {d && (
            <>
              {/* Scoped by UID, so this is everything the account runs — PHP pools, cron jobs,
                  anything a compromised site started. It is per NODE: another node runs its own. */}
              <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Processes" value={String(d.processes)} />
                <Stat label="Threads" value={String(d.threads)} />
                <Stat label="Memory" value={formatBytes(d.rss_bytes)} />
                <Stat label="CPU time" value={`${d.cpu_seconds.toFixed(1)}s`} />
              </div>
              <p className="mb-3 text-[11.5px] text-muted">
                Everything running as <code className="font-mono">{d.system_user}</code> (uid {d.uid}) on{" "}
                <b>{d.node}</b> only. Other nodes run their own copies.
              </p>
              <DataList items={d.list ?? []} columns={cols} getRowKey={(p) => String(p.pid)} emptyMessage="Nothing running." />
            </>
          )}
        </StateView>
      </div>
      <div className="border-t border-line p-4">
        <BandwidthPanel tenantId={tenantId} />
      </div>
    </Section>
  );
}

/** Usage against the plan. */
export function UsageTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tenant-usage", tenantId],
    queryFn: () => client.apiFetch<Usage>(`/tenants/${tenantId}/usage`),
  });
  const rows = q.data?.against_limits ?? [];
  const unmeasured = q.data?.unmeasured ?? [];

  const cols: DataListColumn<LimitRow>[] = [
    { key: "label", label: "Limit", sortable: true, value: (r) => r.label || r.key },
    {
      key: "used",
      label: "Used",
      sortable: true,
      value: (r) => r.used,
      render: (r) => (
        <span className="font-mono text-[12.5px]">
          {r.used}
          {r.unit ? ` ${r.unit}` : ""}
          {(r.incomplete?.length ?? 0) > 0 && (
            <span className="ml-2" title={(r.incomplete ?? []).join("\n")}>
              <StatusPill tone="warn">incomplete</StatusPill>
            </span>
          )}
        </span>
      ),
    },
    {
      key: "limit",
      label: "Allowed",
      sortable: true,
      value: (r) => r.limit,
      // 0 means unlimited, not "none". A bare 0 in this column reads as an account allowed
      // nothing, which is the opposite of the truth.
      render: (r) => (r.limit > 0 ? `${r.limit}${r.unit ? " " + r.unit : ""}` : <span className="text-muted">unlimited</span>),
    },
    {
      key: "percent",
      label: "Of allowance",
      sortable: true,
      value: (r) => r.percent,
      render: (r) =>
        r.limit > 0 ? (
          <StatusPill tone={r.used > r.limit ? "bad" : r.percent > 80 ? "warn" : "ok"}>{Math.round(r.percent)}%</StatusPill>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    { key: "limit_from", label: "Set by", sortable: true, value: (r) => r.limit_from || "", render: (r) => <span className="text-[11.5px] text-muted">{r.limit_from}</span> },
  ];

  return (
    <Section title="Usage against the plan" hint={q.data ? `${q.data.days} days` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="usage">
          <>
            <DataList items={rows} columns={cols} getRowKey={(r) => r.key} emptyMessage="No measured limits." />
            {/* A limit nothing measures is not a limit. Saying so beats an absent row, which
                reads as "within allowance". */}
            {unmeasured.length > 0 && (
              <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                <b>Not measured on this node:</b> {unmeasured.join(", ")}. These limits exist on the plan and nothing
                is currently collecting them, so they cannot be enforced or reported.
              </Panel>
            )}
          </>
        </StateView>
      </div>
    </Section>
  );
}
